import { LoaderFunction } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { AuthContextType } from '../../../../hooks/useAuth';
import { client } from '../../../../gql/client';
import { GET_CONCESSIONS } from '../../../../gql/queries/orders/orders';
import { GET_ROADS } from '../../../../gql/queries/geoPositions/roads';
import { FETCH_SURVEILLANCE_TEAMS } from '../../../../gql/queries/users';
import { readSurveillanceRoutePresets } from '../../../../gql/selectors/surveillanceRoutePresets';
import { selectActiveOperatorsAndVehicles } from '../../../../gql/selectors/actions';
import { preferServerWithCacheFallback } from '../../../../gql/cacheFallback';
import { hoursType } from '../../../../data/orders';

/**
 * Loader for NewRoute workspace.
 * Fetches concessions, roads, operators and vehicles for the selection tables.
 * The actual SR entity is NOT created here — it's created when "Iniciar" is pressed.
 */
export const LoaderNewSurveillanceRoute: (
  userData: AuthContextType | null
) => LoaderFunction = (userData) => async () => {
  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  /**
   * El cuerpo entero, parametrizado por DE DÓNDE salen los datos.
   *
   * 🔴 Salir a patrullar es el peor momento para una rueda infinita: el
   * operario está en la cabina, con cobertura de carretera, y este asistente
   * es la puerta de entrada al turno. `selectActiveOperatorsAndVehicles` son
   * CUATRO consultas a red dentro de este `Promise.all`, así que con lie-fi
   * —la petición que ni responde ni falla— el asistente no llegaba a abrirse.
   * Crear el recorrido sin cobertura YA estaba contemplado (`SaveSurveillanceRoute`
   * se encola y se reproduce); lo que faltaba era poder abrir la pantalla.
   */
  const build = async (mode: 'network' | 'cache') => {
      const policy = mode === 'cache' ? 'cache-only' : 'cache-first';
      const [
        concessionsResponse,
        roadsResponse,
        internalOperators,
        surveillanceTeamsResponse,
        presets,
      ] = await Promise.all([
          client.query({
            query: GET_CONCESSIONS,
            fetchPolicy: policy,
          }),
          client.query({
            query: GET_ROADS,
            fetchPolicy: policy,
          }),
          selectActiveOperatorsAndVehicles(true, {
            fetchPolicy: mode === 'cache' ? 'cache-only' : 'network-only',
          }),
          // V4 FR-3.2 / D14 (audit Disc#10, 2026-05-28): el selector de equipo
          // del RV solo lista equipos `isSurveillance=true` (en UTE, solo
          // ÈXSER). Usamos la query dedicada en vez de todos los equipos que
          // trae `selectActiveOperatorsAndVehicles`. Si solo hay 1, el
          // OperatorsSelectionTable lo autoselecciona y oculta el dropdown.
          client.query({
            query: FETCH_SURVEILLANCE_TEAMS,
            fetchPolicy: policy,
          }),
          // Plantillas de recorrido (2026-08-16): alimentan el selector de
          // nombre, que al elegir una siembra carreteras y PK. NO va
          // `cache-first` como el resto de datos maestros — el porqué, en el
          // selector.
          readSurveillanceRoutePresets(),
        ]);

      const concessions = concessionsResponse.data?.concessions || [];
      const roads = roadsResponse.data?.roads || [];
      const surveillanceTeams = (
        surveillanceTeamsResponse.data?.surveillanceTeams || []
      ).map((tm: { id: string; name: string }) => ({ id: tm.id, name: tm.name }));

      return {
          concessions,
          roads,
          internalOperators: internalOperators ?? { operators: [], vehicles: [], teams: [] },
          externalCompanies: [],
          hoursTypeOptions: hoursType,
          // Solo equipos de vigilancia (no todos los del tenant).
          teams: surveillanceTeams,
          presets,
        };
  };

  // El trabajo entero vive aquí dentro, íntegro. Solo ha dejado de
  // esperarse: la promesa tiene que llegar PENDIENTE al `<Await>` para que
  // el router termine la navegación al instante y el `<Fallback>` enseñe el
  // Loader DENTRO de la pantalla nueva.
  const load = async () => {
  if (userData?.user) {
    try {
      return await preferServerWithCacheFallback(build('network'), async () => {
        const cached = await build('cache');
        // 🔴 Sin catálogos en caché no hay asistente que abrir: los
        // desplegables de carretera y tripulación estarían vacíos y el
        // operario no podría crear nada. Mejor seguir esperando que abrir una
        // pantalla que no sirve.
        return cached.roads.length > 0 ? cached : null;
      });
    } catch (error) {
      Sentry.captureException(error);
      console.error('Error loading new route data:', error);
      return {
          concessions: [],
          roads: [],
          internalOperators: { operators: [], vehicles: [], teams: [] },
          externalCompanies: [],
          hoursTypeOptions: hoursType,
          teams: [],
          presets: [],
        };
    }
  }

  return {
      concessions: [],
      roads: [],
      internalOperators: { operators: [], vehicles: [], teams: [] },
      externalCompanies: [],
      hoursTypeOptions: hoursType,
      teams: [],
      presets: [],
    };
};

  return { data: load() };
};

export default LoaderNewSurveillanceRoute;
